import React, {ReactNode} from 'react';
import {useLocation} from 'react-router-dom';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {RegisterPage} from '@common/auth/ui/register-page';
import {LoginPageWrapper} from '@common/auth/ui/login-page-wrapper';
import {GuestRoute} from '@common/auth/guards/guest-route';
import {useResolvedCustomAuthPage} from '@app/custom-auth-pages/requests/use-resolved-custom-auth-page';
import {CustomAuthThemeProvider} from '@app/custom-auth-pages/custom-auth-theme-provider';

interface Props {
  fallback: ReactNode;
}

export function CustomAuthRouteGate({fallback}: Props) {
  const {pathname} = useLocation();
  const shouldResolve = isRootLevelPath(pathname);
  const query = useResolvedCustomAuthPage(pathname, shouldResolve);

  if (!shouldResolve) {
    return <>{fallback}</>;
  }

  if (query.isLoading) {
    return <FullPageLoader screen />;
  }

  if (!query.data?.page) {
    return <>{fallback}</>;
  }

  const page = query.data.page;

  return (
    <CustomAuthThemeProvider theme={page.theme}>
      {page.type === 'register' ? (
        <RegisterPage customAuthPage={page} />
      ) : (
        <GuestRoute>
          <LoginPageWrapper customAuthPage={page} />
        </GuestRoute>
      )}
    </CustomAuthThemeProvider>
  );
}

function isRootLevelPath(path: string): boolean {
  const normalized = path.trim();
  if (!normalized || normalized === '/') {
    return false;
  }

  return normalized.split('/').filter(Boolean).length === 1;
}
